import { memo } from 'react';
import { getSeatDisplayText } from 'src/components/common/SeatingInfo';
import { ListingNotesDisplay } from 'src/components/Listings/ListingDisplay/ListingNotesDisplay';
import { BoostIconDisplay } from 'src/components/SponsoredListings';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { Stack, TooltipPopover } from 'src/core/ui';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  FormatOption,
  FormatOption_Seating,
} from 'src/modals/EditTableColumns';
import { SeatSaverIcon } from 'src/svgs';
import { AdminHoldIcon } from 'src/svgs/AdminHoldIcon';
import { ClockTrashIcon } from 'src/svgs/ClockTrashIcon';
import { IconsFill, WarningOutlineIcon } from 'src/svgs/Viagogo';
import { TableShimmeringDiv } from 'src/tables/Table/Table.styled';
import { ContentId } from 'src/utils/constants/contentId';
import { Feature, ListingNote, Permission } from 'src/WebApiController';

import { BaseCell } from '../BaseCell';
import { StackedCell } from '../StackedCell';
import * as styles from './SeatingCell.css';

export interface SeatingCellProps {
  section: string;
  row: string | null;
  seatFr: string | null;
  seatTo: string | null;
  isSeatSaver?: boolean;
  adCampaignFlightId?: number | null;
  isWasted?: boolean;
  hasMarketplaceProblem?: boolean;
  isLoading?: boolean;
  userDefinedPrecision?: FormatOption;
  seatTraits?: ListingNote[] | null;
  isUnmappedSection?: boolean;
  showUnmappedSection?: boolean;
  isDuplicate?: boolean;
  showAdminHold?: boolean;
  isAdminHold?: boolean;
  showOrgSeat?: boolean;
  orgSeatFr?: string | null;
  orgSeatTo?: string | null;
  isSoldOut?: boolean;
  overrideSectionRaw?: string | null;
  overrideRowRaw?: string | null;
  overrideSeatsRaw?: string | null;
  isMultiple?: boolean;
}

const SeatingCellComponent = ({
  section,
  row,
  seatFr,
  seatTo,
  isSeatSaver,
  adCampaignFlightId,
  isWasted,
  hasMarketplaceProblem,
  showUnmappedSection,
  isLoading,
  userDefinedPrecision,
  seatTraits,
  isUnmappedSection,
  isDuplicate,
  showAdminHold,
  isAdminHold,
  showOrgSeat,
  orgSeatFr,
  orgSeatTo,
  isSoldOut,
  overrideSectionRaw,
  overrideRowRaw,
  overrideSeatsRaw,
  isMultiple,
}: SeatingCellProps) => {
  const hasSeatTraitsColumnFeature = useUserHasFeature(
    Feature.SeatTraitsColumn
  );
  const { isDarkMode } = useSiteTheme();
  const multipleText = useContent(ContentId.Multiple);

  const hasSponsoredListingsV2Feature = useUserHasFeature(
    Feature.SponsoredListingsV2
  );
  const hasShowSponsoredSalesAsSponsored = useUserHasFeature(
    Feature.ShowSponsoredSalesAsSponsored
  );
  const hasAdPlatformViewPermission = useUserHasAnyOfPermissions(
    Permission.AdPlatform_CampaignsViewAll
  );

  if (isLoading) {
    return <TableShimmeringDiv height="40px" />;
  }

  // Sanitize override values
  const overrideSection =
    overrideSectionRaw && overrideSectionRaw?.trim() !== ''
      ? overrideSectionRaw
      : null;
  const overrideRow =
    overrideRowRaw && overrideRowRaw?.trim() !== '' ? overrideRowRaw : null;
  const overrideSeats =
    overrideSeatsRaw && overrideSeatsRaw?.trim() !== ''
      ? overrideSeatsRaw
      : null;

  if (isMultiple) {
    return <BaseCell>{multipleText}</BaseCell>;
  }
  const isSectionOnly =
    userDefinedPrecision === FormatOption_Seating.Seating_SectionOnly;

  const displayRow = isSectionOnly ? null : row;
  const displaySeatFr = isSectionOnly ? null : seatFr;
  const displaySeatTo = isSectionOnly ? null : seatTo;

  const hasSeats = !isSoldOut && !!(seatFr || seatTo);
  const hasOrgSeats = !!(orgSeatFr || orgSeatTo);

  // When sold out, show original seats instead of current seats
  const seatDisplay =
    isSoldOut && showOrgSeat && hasOrgSeats
      ? getSeatDisplayText({ seatFr: orgSeatFr, seatTo: orgSeatTo })
      : getSeatDisplayText({ seatFr: displaySeatFr, seatTo: displaySeatTo });

  // Calculate which original seats to show (only when NOT sold out)
  const showOrgSeatFr =
    !isSoldOut && showOrgSeat && orgSeatFr && seatFr !== orgSeatFr;
  const showOrgSeatTo =
    !isSoldOut &&
    showOrgSeat &&
    orgSeatTo &&
    seatTo !== orgSeatTo &&
    orgSeatTo !== seatFr;

  // Build icons
  const icons = (
    <Stack gap="s" className={styles.icons}>
      <Stack
        gap="m"
        alignItems="center"
        className={
          hasShowSponsoredSalesAsSponsored &&
          hasAdPlatformViewPermission &&
          adCampaignFlightId
            ? isDarkMode
              ? styles.boostHighlightDark
              : styles.boostHighlight
            : undefined
        }
      >
        {hasShowSponsoredSalesAsSponsored &&
          hasAdPlatformViewPermission &&
          adCampaignFlightId != null && (
            <BoostIconDisplay
              size={vars.iconSize.m}
              style={{ opacity: adCampaignFlightId ? 1 : 0 }}
              inactive={isDarkMode /* we want white in dark mode */}
            />
          )}
        {isUnmappedSection && showUnmappedSection && (
          <TooltipPopover
            variant="link"
            contentVariant="dark"
            triggerContent={
              <WarningOutlineIcon fill={'#FD9927'} size={vars.iconSize.m} />
            }
          >
            <Content id={ContentId.SeatingInfoNotMatchStubHub} />
          </TooltipPopover>
        )}
      </Stack>
      {isWasted && <ClockTrashIcon stroke={IconsFill.currentColor} />}
      {isSeatSaver && (
        <div
          style={{
            backgroundColor: vars.color.surfaceSecondary,
            borderRadius: vars.borderRadius.s,
            padding: vars.spacing.xs,
            width: 'fit-content',
            height: 'fit-content',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <SeatSaverIcon fill={vars.color.iconPrimary} size={vars.iconSize.s} />
        </div>
      )}

      {showAdminHold && isAdminHold && (
        <AdminHoldIcon
          fill={vars.color.textBrand}
          stroke={vars.color.backgroundPrimaryActive}
          size={vars.iconSize.m}
        />
      )}
      {hasMarketplaceProblem && (
        <TooltipPopover
          variant="link"
          triggerContent={
            <WarningOutlineIcon fill={IconsFill.textWarning} withHoverEffect />
          }
        >
          <WarningOutlineIcon
            fill={IconsFill.textWarning}
            size={vars.iconSize.m}
          />
        </TooltipPopover>
      )}
      {isDuplicate && (
        <span className={styles.duplicateLabel}>
          <Content id={ContentId.Duplicate} />
        </span>
      )}
    </Stack>
  );

  // Build the primary display (section)
  const primaryContent = (
    <Stack justifyContent="spaceBetween" alignItems="center" width="full">
      <Stack gap="s" alignItems="center">
        <span>{overrideSection ?? section}</span>
        {(overrideRow || displayRow) && (
          <>
            <span className={styles.primaryLineSoftText}>Row</span>
            <span>{overrideRow ?? displayRow}</span>
          </>
        )}
        {(overrideSeats || seatDisplay) && (
          <>
            <span className={styles.primaryLineSoftText}>Seats</span>
            {showOrgSeatFr && (
              <span className={styles.primaryLineSoftText}>{orgSeatFr}</span>
            )}
            <span>{overrideSeats ?? seatDisplay}</span>
            {showOrgSeatTo && (
              <span className={styles.primaryLineSoftText}>{orgSeatTo}</span>
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
  // Build the secondary display (row, seats, and traits)
  const secondaryContent =
    !isSectionOnly && hasSeatTraitsColumnFeature && seatTraits ? (
      <Stack gap="s" alignItems="center" className={styles.secondaryText}>
        <ListingNotesDisplay seatTraits={seatTraits} />
      </Stack>
    ) : null;

  if (!secondaryContent) {
    // Single line display
    return (
      <BaseCell showPadding>
        <Stack justifyContent="spaceBetween" alignItems="center" width="full">
          {primaryContent}
          {icons}
        </Stack>
      </BaseCell>
    );
  }

  // Multi-line display using stacked layout
  return (
    <StackedCell
      primary={primaryContent}
      secondary={secondaryContent}
      rightContent={icons}
    />
  );
};

SeatingCellComponent.displayName = 'SeatingCell';

export const SeatingCell = memo(SeatingCellComponent);
